import { useState } from "react";
import styles from "./login.module.css";
import { useNavigate } from "react-router-dom";
import { login, adminLogin } from "../../../lib/store";
import { recordLoginGreeting } from "../../../lib/cookies";
import Navbar from "../../LandingPage/Navbar/Navbar";
import Footer2 from "../../Footer2/Footer2";

export default function Login({ embedded = false, onSuccess } = {}) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();

  const set = (e) => {
    const { name, value } = e.target;
    if (name === "email" && /\s/.test(value)) return;
    if (name === "password" && /\s/.test(value)) return;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError("Email and password are required.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      // Admin gate uses a dedicated login that only checks the internal team
      // (admins.json); the public portal uses the HubSpot-backed login.
      const data = embedded ? await adminLogin(form) : await login(form);
      sessionStorage.setItem("user", JSON.stringify(data.user));
      sessionStorage.setItem("email", data.user?.email || form.email);
      recordLoginGreeting(data.user?.email);
      // Embedded (admin gate): the endpoint already guarantees an internal
      // account — hand control back to the parent to show the dashboard.
      if (embedded) {
        if (onSuccess) onSuccess(data.user);
        return;
      }
      // Standalone: admins land on the admin panel; others on the dashboard.
      if (data.user?.role_type === "Admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.message || "Server error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className={styles.page}>
        {/* ── Right form panel ── */}
        <div className={styles.rightPanel}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h1 className={styles.title}>
                {embedded ? "Admin Panel" : "Doctor Portal"}
              </h1>
              <p className={styles.subtitle}> Login to your account</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {error && <div className={styles.errorBox}>⚠️ {error}</div>}

              <div className={styles.field}>
                <label className={styles.label}>Email Address</label>
                <input
                  className={styles.input}
                  type="email"
                  name="email"
                  placeholder="dr@clinic.com"
                  value={form.email}
                  onChange={set}
                  autoComplete="email"
                  disabled={loading}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Password</label>
                <div className={styles.passWrap}>
                  <input
                    className={styles.input}
                    type={showPass ? "text" : "password"}
                    name="password"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={set}
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPass(!showPass)}
                  >
                    {showPass ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
              </div>

              {!embedded && (
                <div className={styles.forgotRow}>
                  <span className={styles.link} onClick={() => navigate("/forgot-password")}>
                    Forgot Password?
                  </span>
                </div>
              )}

              <button className={styles.btn} type="submit" disabled={loading}>
                {loading ? (
                  <span className={styles.btnInner}>
                    <span className={styles.spinner} /> Verifying...
                  </span>
                ) : (
                  "Login →"
                )}
              </button>
            </form>

            {!embedded && (
              <div className={styles.formFooter}>
                Don't have an account?{" "}
                <span className={styles.link} onClick={() => navigate("/signup")}>
                  Register here
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer2 />
    </>
  );
}
